import React, { Component, useRef, useEffect } from 'react';
import { useLoadScript, StandaloneSearchBox } from '@react-google-maps/api';
import { Input } from 'antd';

const API_GOOGLE_MAP = process.env.REACT_APP_API_GOOGLE_MAP;

// Static libraries array to prevent LoadScript reload
const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry', 'drawing'];

const getAddressComponent = (addressComponents, field) => {
  const result = addressComponents.find(item => item.types.includes(field));
  return result ? result.long_name : '';
};

const AddressSearchBoxComponent = ({ value, placeholder, disabled, onFocus, separate, onChange, bounds }) => {
  const searchBoxRef = useRef(null);
  const inputRef = useRef(null);

  // Extract API key from URL or use directly if it's just a key
  const getApiKey = () => {
    if (!API_GOOGLE_MAP) return '';
    try {
      const url = new URL(API_GOOGLE_MAP);
      return url.searchParams.get('key') || '';
    } catch {
      // If it's not a URL, assume it's the key itself
      return API_GOOGLE_MAP;
    }
  };

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: getApiKey(),
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  useEffect(() => {
    if (isLoaded && searchBoxRef.current) {
      const places = searchBoxRef.current.getPlaces();
      if (places && places.length > 0) {
        if (separate) {
          const addressComponents = places[0].address_components;
          const streetNumber = getAddressComponent(addressComponents, 'street_number');
          const streetName = getAddressComponent(addressComponents, 'route');
          const city = getAddressComponent(addressComponents, 'locality');
          const postcode = getAddressComponent(addressComponents, 'postal_code');
          const state = getAddressComponent(addressComponents, 'administrative_area_level_1');
          const country = getAddressComponent(addressComponents, 'country');
          onChange({ address: `${streetNumber} ${streetName}`, city, postcode, state, country });
        } else {
          const address = places[0].formatted_address;
          onChange(address);
        }
      }
    }
  }, [isLoaded, separate, onChange]);

  const onPlacesChanged = () => {
    if (searchBoxRef.current) {
      const places = searchBoxRef.current.getPlaces();
      if (places && places.length > 0) {
        if (separate) {
          const addressComponents = places[0].address_components;
          const streetNumber = getAddressComponent(addressComponents, 'street_number');
          const streetName = getAddressComponent(addressComponents, 'route');
          const city = getAddressComponent(addressComponents, 'locality');
          const postcode = getAddressComponent(addressComponents, 'postal_code');
          const state = getAddressComponent(addressComponents, 'administrative_area_level_1');
          const country = getAddressComponent(addressComponents, 'country');
          onChange({ address: `${streetNumber} ${streetName}`, city, postcode, state, country });
        } else {
          const address = places[0].formatted_address;
          onChange(address);
        }
      }
    }
  };

  if (!isLoaded) {
    return <div style={{ height: '100%' }} />;
  }

  return (
    <div data-standalone-searchbox="">
      <StandaloneSearchBox
        onLoad={(ref) => {
          searchBoxRef.current = ref;
        }}
        bounds={bounds}
        onPlacesChanged={onPlacesChanged}
      >
        <Input
          ref={inputRef}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
        />
      </StandaloneSearchBox>
    </div>
  );
};

export default class AddressSearchBox extends Component {
  onAddressChange = e => {
    const value = e.target ? e.target.value : e;
    const { onChange } = this.props;
    if (onChange) {
      onChange(value);
    }
  };

  render() {
    const { value, placeholder, disabled, onFocus, separate, bounds } = this.props;
    return (
      <AddressSearchBoxComponent
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={this.onAddressChange}
        onFocus={onFocus}
        separate={separate}
        bounds={bounds}
      />
    );
  }
}
